/**
 * My Bots (§7.1) — the owner dashboard. It is not a role-scoped dashboard: it is
 * one side of a per-bot relationship, and the same user's rentals live under My
 * Rentals with the same shape.
 */

import { Link } from 'react-router-dom';
import { EMPTY_STATES } from '@pm/shared';

import { useApi } from '../../hooks/useApi';
import type { Bot, Page, Rental } from '../../api/types';
import { Card, Loading, Badge } from '../../components/ui';
import { RuntimeBadge, worstAccount, healthCopy } from '../../components/health';
import { EmptyState } from '../../components/EmptyState';
import { ErrorView } from '../../components/ErrorView';
import { amountLabel, localDateTime, percent } from '../../lib/format';
import { DECLARED_LABEL } from '../../lib/vocabulary';
import { routes } from '../../api/routes';

export function MyBots() {
  const bots = useApi<Page<Bot>>(routes.bots.collection, { query: { limit: 50 } });
  // Requests waiting on this owner. Also mirrored as action-required notifications.
  const requests = useApi<Page<Rental>>(routes.rentals.collection, {
    query: { role: 'owner', status: 'requested', limit: 20 },
    pollMs: 60_000,
  });

  if (bots.initialLoading) return <Loading label="Loading your bots" />;
  if (bots.problem) return <ErrorView problem={bots.problem} onRetry={bots.reload} />;

  const list = bots.data?.data ?? [];
  const pending = requests.data?.data ?? [];

  return (
    <div className="my-bots">
      <header className="page-head">
        <h1>My Bots</h1>
        {list.length > 0 ? (
          <Link className="btn btn-primary" to="/bots/new">
            Create a bot
          </Link>
        ) : null}
      </header>

      {pending.length > 0 ? (
        <Card title={`${pending.length} rental request(s) waiting on you`} tone="warn">
          <ul className="request-list">
            {pending.map((r) => (
              <li key={r.id} className="request-row">
                <div>
                  <p>
                    <strong>{r.bot_name}</strong> — @{r.renter_handle} wants{' '}
                    {localDateTime(r.start_at)} to {localDateTime(r.end_at)}
                  </p>
                  <p className="muted">
                    {DECLARED_LABEL}: {amountLabel(r.declared)} · request expires{' '}
                    {localDateTime(r.request_expires_at)}
                  </p>
                </div>
                <Link className="btn btn-primary btn-sm" to={`/rentals/${r.id}/approval`}>
                  Review
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {list.length === 0 ? (
        <Card>
          <EmptyState
            heading={EMPTY_STATES.ownerNoBots.heading}
            body={EMPTY_STATES.ownerNoBots.body}
            cta={{ label: EMPTY_STATES.ownerNoBots.cta, to: '/bots/new' }}
          />
        </Card>
      ) : (
        <ul className="bot-grid">
          {list.map((b) => {
            const bad = worstAccount(b.social_accounts);
            return (
              <li key={b.id} className="bot-card">
                <div className="bot-card-head">
                  <Link to={`/bots/${b.id}/console`}>
                    <h2>{b.name}</h2>
                  </Link>
                  <RuntimeBadge state={b.runtime_state} />
                </div>
                <p className="muted">
                  @{b.slug} · {b.status} · {b.visibility}
                </p>
                <p>
                  <Badge tone={b.health_score > 0.9 ? 'ok' : b.health_score > 0.7 ? 'warn' : 'bad'}>
                    health {percent(b.health_score, 0)}
                  </Badge>
                  {b.active_rental ? (
                    <Badge tone="info">
                      rented to @{b.active_rental.renter_handle}
                    </Badge>
                  ) : null}
                </p>
                {bad ? (
                  <p className="failure-reason">
                    {bad.platform} @{bad.external_handle}: {healthCopy(bad.status).state}.{' '}
                    {healthCopy(bad.status).action}
                  </p>
                ) : null}
                <nav className="bot-card-links" aria-label={`${b.name} sections`}>
                  <Link to={`/bots/${b.id}/console`}>Console</Link>
                  <Link to={`/bots/${b.id}/accounts`}>Accounts</Link>
                  <Link to={`/bots/${b.id}/listing`}>Listing</Link>
                  <Link to={`/bots/${b.id}/activity`}>Activity</Link>
                </nav>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
